import { AlertCircle, CheckCircle, FileText, Upload } from "lucide-react";
import React, { useState } from "react";

interface ScooterDriverCardProps {
  isUploading?: boolean;
  onFileUpload: (documentType: string, file: File) => void;
  uploadedDocuments?: { [key: string]: boolean };
}

const ScooterDriverCard: React.FC<ScooterDriverCardProps> = ({
  isUploading = false,
  onFileUpload,
  uploadedDocuments = {},
}) => {
  const [dragOver, setDragOver] = useState<null | string>(null);

  const requiredDocuments = [
    {
      description: "Valid motorcycle driving license (Class A1, A2, or A)",
      id: "driving_license",
      required: true,
      title: "Motorcycle License",
    },
    {
      description: "Current vehicle registration for scooter or motorbike",
      id: "scooter_registration",
      required: true,
      title: "Scooter/Motorbike Registration",
    },
    {
      description: "Valid third-party or comprehensive insurance",
      id: "vehicle_insurance",
      required: true,
      title: "Vehicle Insurance",
    },
    {
      description: "Valid roadworthy certificate (not older than 12 months)",
      id: "roadworthy_certificate",
      required: true,
      title: "Roadworthy Certificate",
    },
    {
      description: "National ID or passport",
      id: "identity_document",
      required: true,
      title: "Identity Document",
    },
    {
      description: "Photos of helmet, reflective vest, and safety equipment",
      id: "safety_gear_compliance",
      required: true,
      title: "Safety Gear Compliance",
    },
    {
      description: "Food safety compliant delivery box certification",
      id: "delivery_box_certificate",
      required: false,
      title: "Delivery Box Certificate",
    },
  ];

  const handleFileSelect = (documentType: string, file: File) => {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (file && allowedTypes.includes(file.type)) {
      if (file.size <= 4.5 * 1024 * 1024) {
        // 4.5MB limit
        onFileUpload(documentType, file);
      } else {
        alert("File size must be less than 4.5MB");
      }
    } else {
      alert("Please select a PDF, image file (JPG, PNG), or Word document");
    }
  };

  const handleDrop = (e: React.DragEvent, documentType: string) => {
    e.preventDefault();
    setDragOver(null);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(documentType, files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent, documentType: string) => {
    e.preventDefault();
    setDragOver(documentType);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
      <div className="mb-6 flex items-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
          <FileText className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Scooter Driver Documents
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Upload required documents for scooter/motorbike driver registration
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {requiredDocuments.map((doc) => (
          <div
            className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
            key={doc.id}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                  {doc.title}
                  {doc.required && <span className="ml-1 text-red-500">*</span>}
                </h4>
                {uploadedDocuments[doc.id] && (
                  <CheckCircle className="ml-2 h-5 w-5 text-green-500" />
                )}
                {doc.required && !uploadedDocuments[doc.id] && (
                  <AlertCircle className="ml-2 h-5 w-5 text-red-500" />
                )}
              </div>
            </div>

            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              {doc.description}
            </p>

            <div
              className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
                dragOver === doc.id
                  ? "border-orange-400 bg-orange-50 dark:bg-orange-900/20"
                  : uploadedDocuments[doc.id]
                    ? "border-green-300 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
              }`}
              onDragLeave={handleDragLeave}
              onDragOver={(e) => handleDragOver(e, doc.id)}
              onDrop={(e) => handleDrop(e, doc.id)}
            >
              {uploadedDocuments[doc.id] ? (
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <span className="ml-2 text-green-600 dark:text-green-400">
                    Document uploaded successfully
                  </span>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <label
                      className="cursor-pointer text-sm font-medium text-orange-600 hover:text-orange-500"
                      htmlFor={`file-${doc.id}`}
                    >
                      Drop files here or click to upload
                    </label>
                    <input
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      disabled={isUploading}
                      id={`file-${doc.id}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileSelect(doc.id, file);
                        }
                      }}
                      type="file"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    PDF, PNG, JPG, DOC, DOCX up to 4.5MB
                  </p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
        <div className="flex items-start">
          <AlertCircle className="mt-0.5 h-5 w-5 text-amber-500" />
          <div className="ml-2">
            <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Safety Requirements:
            </h5>
            <ul className="mt-1 space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <li>• Helmet must be DOT or ECE certified</li>
              <li>• Reflective vest required for night deliveries</li>
              <li>• Scooter must be roadworthy and insured</li>
              <li>• Safety gear photos must show clear visibility</li>
              <li>• Food delivery box must meet hygiene standards</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScooterDriverCard;
